import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../contexts/NavigationContext';
import BottomNavBar from '../components/BottomNavBar';

/**
 * NexusMobileFrame - Enforces 1-page no-scroll layout contract
 * Layout: Content (flex-1) + BottomNav (64px) = 100dvh
 * Mobile viewport: 390×844, no vertical scroll
 * Each page includes its own header internally
 */
const NexusMobileFrame = ({ children, showHeader = false }) => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { contentKey } = useNavigation();
  
  // Don't show frame on login page
  const isLoginPage = location.pathname === '/login';
  const isTeleprompterPage = location.pathname.includes('/teleprompter');
  const showNavigation = isAuthenticated && !isLoginPage && !isTeleprompterPage;
  
  // SCROLLABLE EDITOR PAGES - These pages need full scrolling enabled
  const isScrollableEditorPage = location.pathname.includes('/ambassador-packs/v4') || 
                                   location.pathname.includes('/product-packs/v4') ||
                                   location.pathname.includes('/product-packs/editor');

  if (isLoginPage || isTeleprompterPage) {
    return children;
  }
  
  // For editor pages, return without mobile frame constraints
  // Need to override global no-scroll CSS with useEffect
  if (isScrollableEditorPage) {
    // Force enable scrolling on mount for editor pages
    React.useEffect(() => {
      const originalBodyOverflow = document.body.style.overflow;
      const originalBodyOverflowY = document.body.style.overflowY;
      const originalBodyHeight = document.body.style.height;
      
      // Force scrolling with inline styles (overrides CSS)
      document.body.style.setProperty('overflow-y', 'auto', 'important');
      document.body.style.setProperty('height', 'auto', 'important');
      document.documentElement.style.setProperty('overflow-y', 'auto', 'important');
      document.documentElement.style.setProperty('height', 'auto', 'important');
      
      return () => {
        // Restore original values when leaving editor
        document.body.style.overflow = originalBodyOverflow;
        document.body.style.overflowY = originalBodyOverflowY;
        document.body.style.height = originalBodyHeight;
      };
    }, []);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        {children}
      </div>
    );
  }

  return (
    <div 
      className="h-screen h-[100dvh] flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50"
      style={{
        '--header-height': '0px',
        '--nav-height': '56px',
        '--safe-top': 'env(safe-area-inset-top)',
        '--safe-bottom': 'env(safe-area-inset-bottom)'
      }}
    >
      {/* Header space optimized - Governance now in dashboard header */}
      
      {/* Header Region - 56px Fixed */}
      {showHeader && (
        <header 
          className="h-14 sticky top-0 z-40 flex items-center justify-between px-4 bg-white/80 backdrop-blur-sm border-b border-gray-200 shrink-0"
          style={{ 
            paddingTop: 'env(safe-area-inset-top)',
            height: 'calc(56px + env(safe-area-inset-top))'
          }}
        >
          {/* Header content will be passed via context or props if needed */}
        </header>
      )}

      {/* Main Content Region - Flexible, Scrollable within bounds */}
      <main 
        className="flex-1 overflow-y-auto overflow-x-hidden relative no-transform"
        style={{
          height: showNavigation 
            ? 'calc(100dvh - 56px - env(safe-area-inset-bottom))' 
            : 'calc(100dvh - env(safe-area-inset-bottom))',
          paddingBottom: showNavigation ? 'calc(56px + env(safe-area-inset-bottom))' : '0'
        }}
      >
        {/* Page Content Container - Force re-render on tab change */}
        <div key={contentKey} className="h-full overflow-y-auto">
          {children}
        </div>
      </main>

      {/* Bottom Navigation - 56px Compact + Safe Area */}
      {showNavigation && (
        <div 
          className="fixed bottom-0 left-0 right-0 z-[100] shrink-0 bg-white"
          style={{ 
            height: 'calc(56px + env(safe-area-inset-bottom))',
            minHeight: '56px'
          }}
        >
          <BottomNavBar />
        </div>
      )}
    </div>
  );
};

export default NexusMobileFrame;